import React, { useEffect, useState } from 'react'

import { Html } from '@application/Interfaces/Atoms/Html'
import { useGetTracking } from '@application/Interfaces/Hooks/useGetTracking'
import { useTranslation } from '@application/Interfaces/Hooks/useTranslation'
import { TrackingSectionType } from '@application/Types/ComponentTypes'
import { Grid } from '@icontainers/design-system/dist/atoms/Grid'
import { Input } from '@icontainers/design-system/dist/molecules/Input'
import { useWidth } from '@icontainers/design-system/dist/utils'
import { Nullable } from '@src/Application/Types/Opt'
import { GetTrackAndTraceError, TrackAndTraceEvent } from '@src/Infrastructure/Types/GetTrackAndTrace'

import {
    Accordion,
    Button,
    Container,
    ContentColumn,
    EvenRow,
    Highlight,
    InfoColumn,
    InfoRow,
    InputWrapper,
    Note,
    ShipmentActivityColumn,
    ShipmentInformationColumn,
    SmallTitle,
    TimeColumn,
    Title,
    Wrapper,
} from './TrackingSection.styled'
import { fetchData, handleSubmit, handleValueChange, mapTrackingEventToShipmentActivity } from './utils'

export const TrackingSection = (props: TrackingSectionType): JSX.Element => {
    const {
        title,
        inputLabel,
        buttonLabel,
        shipmentActivityTitle,
        shipmentInformationTitle,
        note,
        shipmentInformation,
        defaultShipmentId = '',
    } = props

    const { t } = useTranslation()
    const { isMobile } = useWidth()
    const [shipmentId, setShipmentId] = useState<string>(defaultShipmentId)
    const [value, setValue] = useState<string>(defaultShipmentId)
    const [events, setEvents] = useState<Nullable<TrackAndTraceEvent[]>>()
    const [error, setError] = useState<GetTrackAndTraceError | null>(null)
    const { refetch, isFetching, isFetched } = useGetTracking({ shipmentId })

    useEffect(() => {
        shipmentId && fetchData({ refetch, setEvents, setError })
    }, [shipmentId])

    const shipmentActivityContent = mapTrackingEventToShipmentActivity(events).map((activity, index) => (
        <Grid container component={index % 2 === 1 ? EvenRow : undefined} key={index}>
            <Grid item component={TimeColumn}>
                <Highlight>{activity.date}</Highlight>
                {activity.time}
            </Grid>
            <Grid item component={ContentColumn}>
                <Highlight>{activity.event}</Highlight>
                {activity.location}
            </Grid>
        </Grid>
    ))

    const shipmentInformationContent = shipmentInformation?.map((info, index) => (
        <Grid container component={InfoRow} key={index}>
            <Grid item>{info.label}</Grid>
            <Grid item component={InfoColumn}>
                <Highlight>{info.value}</Highlight>
            </Grid>
        </Grid>
    ))

    const rightSpacing = shipmentInformation?.length ? 10 : 0

    return (
        <Container>
            <Wrapper>
                <Title>{title}</Title>
                <InputWrapper>
                    <Input
                        label={inputLabel}
                        name="tracking-input"
                        value={value}
                        onChange={handleValueChange(setValue)}
                    />
                    <Button
                        mode="Dark"
                        fullWidth
                        onClick={handleSubmit(value, setShipmentId)}
                        disabled={isFetching}
                        isLoading={isFetching}
                    >
                        {buttonLabel}
                    </Button>
                </InputWrapper>
                {error && (
                    <Note
                        type="alert"
                        message={error === 'SHIPMENT_NOT_FOUND' ? t('shipmentNotFound') : t('requestError')}
                    />
                )}
            </Wrapper>

            {!events?.length && isFetched && <Note type="warning" message={t('noShipmentResults')} />}

            {Boolean(events?.length) && (
                <>
                    {note && isMobile && (
                        <Note type="warning" message={<Html content={note.data.childMarkdownRemark.html} />} />
                    )}
                    <Grid container>
                        <Grid
                            item
                            xs={12}
                            mdSpacingRight={rightSpacing}
                            lgSpacingRight={rightSpacing}
                            component={ShipmentActivityColumn}
                        >
                            {isMobile && (
                                <Accordion isOpen title={shipmentActivityTitle}>
                                    {shipmentActivityContent}
                                </Accordion>
                            )}
                            {!isMobile && (
                                <>
                                    <SmallTitle>{shipmentActivityTitle}</SmallTitle>
                                    {note && (
                                        <Note
                                            type="warning"
                                            message={<Html content={note.data.childMarkdownRemark.html} />}
                                        />
                                    )}
                                    {shipmentActivityContent}
                                </>
                            )}
                        </Grid>
                        {Boolean(shipmentInformation?.length) && (
                            <Grid item xs={12} component={ShipmentInformationColumn}>
                                {isMobile && (
                                    <Accordion title={shipmentInformationTitle}>{shipmentInformationContent}</Accordion>
                                )}
                                {!isMobile && (
                                    <>
                                        <SmallTitle>{shipmentInformationTitle}</SmallTitle>
                                        {shipmentInformationContent}
                                    </>
                                )}
                            </Grid>
                        )}
                    </Grid>
                </>
            )}
        </Container>
    )
}
